import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { OverflowTooltip } from "../../../components/ui";

export interface AppointmentTextCellProps {
  /** What the cell says, or `null` where the record has nothing to put in it. */
  text: string | null;
  /** What the hover box calls it — "Reason", "Address". */
  heading: string;
}

/**
 * A table cell holding prose: one line, and the rest on hover.
 *
 * One line because these stand in a table whose every other row is two lines
 * tall, and a reason or an address that ran to three would push the rows
 * around it out of line — a history is read by scanning down it, and rows of
 * different heights are what makes that hard. So the cell is clipped, and the
 * tooltip is where the sentence is finished.
 *
 * The tooltip only exists where there is something to finish. A box that opens
 * on every row to repeat a line already fully visible is a box that teaches
 * people not to hover, so the cell measures itself and stays quiet when the
 * text fits.
 *
 * Four columns use it and they are different questions — what an appointment
 * was for, why it was called off, what was written down when it was marked,
 * and where an in-person one is held — which is why the heading is passed in
 * rather than assumed. An address arrives with its own line breaks
 * (`"Clinic 4, MG Road
Pune 411001"`); `truncate` sets `white-space: nowrap`,
 * so those read as spaces here and the whole of it is in the tooltip.
 */
const AppointmentTextCell: React.FC<AppointmentTextCellProps> = ({
  text,
  heading,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [clipped, setClipped] = useState(false);

  /**
   * Whether the line is cut off, measured rather than guessed from its length:
   * how much fits depends on the column's width and the glyphs in it, neither
   * of which a character count knows about.
   *
   * In a layout effect, so it is read after the row is laid out and before the
   * browser paints — a measurement taken a frame later would show the tooltip
   * hint appearing after the row had already been drawn without one.
   *
   * The `+ 1` is slack for sub-pixel widths, which round apart on a scaled
   * display and would otherwise report every cell as clipped.
   */
  useLayoutEffect(() => {
    const node = ref.current;
    setClipped(!!node && node.scrollWidth > node.clientWidth + 1);
  }, [text]);

  // The column is a percentage of the table, so the same sentence fits at one
  // window width and not at another. Re-measured on resize rather than watched
  // with a `ResizeObserver`: one listener for the whole table beats one
  // observer per row, and the width only ever changes with the window.
  useEffect(() => {
    const remeasure = () => {
      const node = ref.current;
      setClipped(!!node && node.scrollWidth > node.clientWidth + 1);
    };

    window.addEventListener("resize", remeasure);
    return () => window.removeEventListener("resize", remeasure);
  }, []);

  // A blank is neither a reason nor an address — both dialogs mark their field
  // optional, so plenty of rows have none, and an empty cell would read as one
  // lost rather than as one never asked for.
  if (!text) return <span className="text-ink-muted">-</span>;

  return (
    <>
      <span
        ref={ref}
        className="block truncate"
        // Reachable by keyboard only where there is something behind the
        // line: a tab stop on every row of a table, most of which would open
        // a box repeating what is already on screen, is a table nobody can
        // get past. Focus opens it for the same reason hover does.
        tabIndex={clipped ? 0 : undefined}
        onMouseEnter={() => clipped && setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => clipped && setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {text}
      </span>
      {/* The app's own hover box rather than a `title`, so this reads like
          every other thing on the screen that is only showing part of itself.
          One item, because both of these are one thing and not a list. */}
      <OverflowTooltip
        anchorRef={ref}
        open={open}
        heading={heading}
        items={[text]}
      />
    </>
  );
};

export default AppointmentTextCell;
